import React, { FC, useMemo } from 'react';
import { Space } from 'antd';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';

import { Form, Tabs } from '~/components';
import { BusinessIcon, IndividualsIcon } from '~/img';
import { useGetApplication, useGetPayloadWithProvided } from '~/controllers/wizard';
import BeneficialOwnerAdditionalInformation from '~/components/views/ShareholdersMultiple/BeneficialOwnerAdditionalInformation';
import { getStagePayload } from '~/utils/getStagePayload';
import { KsaShareholders } from '~/types/ksa/KsaShareholders';
import IndividualTableForm from './individuals/IndividualTableForm';
import LegalEntitiesTableForm from './legalEntities/LegalEntitiesTableForm';
import BeneficialOwnerTableForm from './beneficialOwners/BeneficialOwnerTableForm';

const ShareTables: FC = () => {
  const { t } = useTranslation('ksaApplication');

  const applicationQuery = useGetApplication();
  const payload = useGetPayloadWithProvided();
  const stagePayload = getStagePayload.wizard(applicationQuery.data);
  const errorFields = stagePayload?.activeStep.errorInformation?.errorFields;
  const individualHasWarnings = useMemo(() => {
    if (!errorFields) return false;
    return !!errorFields.find((errorObj) =>
      Object.keys(errorObj).find((namePath) => namePath.includes('individuals'))
    );
  }, [errorFields]);
  const legalEntitiesHasWarnings = useMemo(() => {
    if (!errorFields) return false;
    return !!errorFields.find((errorObj) =>
      Object.keys(errorObj).find(
        (namePath) => namePath.includes('legalEntities') || namePath.includes('beneficialOwners')
      )
    );
  }, [errorFields]);

  const shareholders = payload?.shareholders as KsaShareholders;
  const isIndividualsTabDisabled = isEmpty(shareholders?.individuals);
  const isLegalEntityTabDisabled = isEmpty(shareholders?.legalEntities);

  return (
    <>
      <StyledTabs
        sticky
        centered
        defaultActiveKey={isIndividualsTabDisabled ? legalEntityTabKey : individualsTabKey}
      >
        <StyledTabs.TabPane
          tab={
            <span data-warning={individualHasWarnings.toString()}>
              <IndividualsIcon />
              {t('Individual shareholders')}
            </span>
          }
          disabled={isIndividualsTabDisabled}
          key={individualsTabKey}
        >
          <div className="ShareTables-TabContent">
            <Form.Item name={['shareholders']} noStyle>
              <IndividualTableForm />
            </Form.Item>
          </div>
        </StyledTabs.TabPane>
        <StyledTabs.TabPane
          tab={
            <span data-warning={legalEntitiesHasWarnings.toString()}>
              <BusinessIcon />
              {t('Legal entity shareholders')}
            </span>
          }
          key={legalEntityTabKey}
          disabled={isLegalEntityTabDisabled}
        >
          <div className="ShareTables-TabContent">
            <Space size={56} direction="vertical" style={fullWidth}>
              <Form.Item name={['shareholders']} noStyle>
                <LegalEntitiesTableForm />
              </Form.Item>
              <Form.Item name={['shareholders']} noStyle>
                <BeneficialOwnerTableForm />
              </Form.Item>
              <BeneficialOwnerAdditionalInformation />
            </Space>
          </div>
        </StyledTabs.TabPane>
      </StyledTabs>
    </>
  );
};

const individualsTabKey = 'individualsTabKey';
const legalEntityTabKey = 'legalEntityTabKey';

const fullWidth = { width: '100%' };

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px;
    z-index: 3;
  }

  .ant-table-empty {
    tbody {
      display: none;
    }
  }

  .ShareTables-TabContent {
    max-width: calc(100vw - ${16 * 2 + 8 * 2}px);
  }
`;

export default ShareTables;
